/* Delegated logic for snippets/product-quick-overlay.liquid.
   Loaded once (browsers dedupe repeated same-src tags); serves every current and
   future overlay instance, including markup injected via innerHTML (lazy cart
   drawer), because all handlers are delegated on document and all per-instance
   config is read from the DOM at event time.

   Single data source per instance: the overlay root's data-pqo-variants
   attribute — { "<variantId>": { qty, available, title, price, options } } —
   plus the scalar data-pqo-default-variant attribute. The qty-breaks variant
   map, the multi-option variants array and the option count are all derived
   from it.

   Open/close, countdown, stock status and single-option radio behavior live in
   snippets/product-quick-overlay-js.liquid (which also reads data-pqo-variants
   and exposes window._pqoSetHeaderPrice). */
(function () {
  'use strict';
  if (window._pqoDelegatedInit) return;
  window._pqoDelegatedInit = true;

  /* ---------- shared helpers ---------- */

  function variantData(overlay) {
    try { return JSON.parse(overlay.getAttribute('data-pqo-variants')) || {}; } catch (e) { return {}; }
  }

  /* [{ id, options, qty, available, price }, ...] derived from data-pqo-variants */
  function variantList(overlay) {
    var V = variantData(overlay);
    return Object.keys(V).map(function (id) {
      var v = V[id] || {};
      return {
        id: parseInt(id, 10),
        options: v.options || [],
        qty: v.qty || 0,
        available: !!v.available,
        price: v.price
      };
    });
  }

  function defaultVariantId(overlay) {
    return parseInt(overlay.getAttribute('data-pqo-default-variant'), 10) || null;
  }

  /* EUR without trailing zeros, comma decimal — must match Liquid money_without_trailing_zeros output */
  function formatPrice(cents) {
    var p = (cents / 100).toFixed(2).replace(/\.00$/, '').replace('.', ',');
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    return cur === 'SEK' ? p + ' kr' : p + '€';
  }

  /* Attribution for anything bought through this sheet.

     `_upsell_source` is underscore-prefixed, so Shopify hides it from the cart and
     checkout the shopper sees, while it stays on the line item in the admin order —
     which is where "did the upsell convert?" gets answered. Same property names the
     sheet's own form and the attached add-on cards (assets/cart-drawer-lazy.js)
     use, so every surface reports through one set of fields. The values are set
     when the sheet opens; see snippets/product-quick-overlay-js.liquid. */
  function addItemPayload(overlay, vid, qty) {
    var item = { id: vid, quantity: qty };
    var source = overlay && overlay.getAttribute('data-pqo-source');

    if (source) {
      item.properties = { _upsell_source: source };
      ['reason', 'for'].forEach(function (key) {
        var value = overlay.getAttribute('data-pqo-' + key);
        if (value) item.properties['_upsell_' + key] = value;
      });
    }

    return item;
  }

  function postAdd(btn, vid, qty, opts) {
    btn.disabled = true;
    btn.style.opacity = '0.6';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [addItemPayload(document.getElementById(opts.did), vid, qty)] })
    })
      .then(function (r) { if (!r.ok) throw new Error(); return r.json(); })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        var overlay = document.getElementById(opts.did);
        if (overlay) {
          overlay.classList.remove('is-open');
          setTimeout(function () { overlay.style.display = 'none'; }, 300);
        }
        if (opts.resetOverflow) document.body.style.overflow = '';
        var cart = document.getElementById('cart-drawer');
        if (cart && cart.show) setTimeout(function () { cart.show(); }, opts.showDelay);
      })
      .catch(function (err) { console.error(opts.errLabel, err); })
      .finally(function () { btn.disabled = false; btn.style.opacity = ''; });
  }

  /* ---------- sheets fetched on demand ---------- */

  /* A card marked [data-pqo-lazy="<handle>"] promises a sheet that is not on the
     page yet. It is fetched from sections/quick-buy-overlay.liquid the first
     time the shopper reaches for it — hover, touch or focus, whichever comes
     first, so the request is usually done before the tap lands.

     Rendering them all up front cost the cart drawer 274 KB of its 300 KB, on
     every cart change, for the one sheet a shopper actually opens. */

  var LAZY_ATTR = 'data-pqo-lazy';
  var lazyRequests = {};

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Outside the drawer, like the placeholder these used to live in: a sheet
     inside it is clipped by it. */
  function overlayHost() {
    var host = document.getElementById('pqo-lazy-overlays');
    if (!host) {
      host = document.createElement('div');
      host.id = 'pqo-lazy-overlays';
      document.body.appendChild(host);
    }
    return host;
  }

  function fetchOverlay(handle) {
    if (lazyRequests[handle]) return lazyRequests[handle];

    lazyRequests[handle] = fetch(rootUrl() + 'products/' + handle + '?section_id=quick-buy-overlay', {
      headers: { Accept: 'text/html' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('quick-buy-overlay ' + response.status);
        return response.text();
      })
      .then(function (html) {
        // Detached div + innerHTML: nothing in the response ever executes.
        var holder = document.createElement('div');
        holder.innerHTML = html;

        var overlay = holder.querySelector('.pqo-overlay');
        if (!overlay) throw new Error('quick-buy-overlay: no sheet in response');

        // A sheet for this product may have arrived by another route already.
        if (!document.getElementById(overlay.id)) {
          overlayHost().appendChild(overlay);
          initOverlay(overlay);
        }
      })
      .catch(function (error) {
        delete lazyRequests[handle]; // let a later tap try again
        throw error;
      });

    return lazyRequests[handle];
  }

  /* The sheet's colourway row, built from the card's own swatches.

     The sheet is fetched per product, and a product has no idea which group it
     was offered in — the group only exists in the cart section's settings. The
     card on screen already shows every colour in it, and each swatch does
     nothing but open that colour's sheet, so the row is a copy of what is
     already there rather than something the sheet has to be told. */
  /* The swatches to copy: the card's own when the sheet was opened from one, or
     the row in the sheet being switched away from — a shopper going Black →
     Cream must still be able to go on to Fudge. */
  function colourSourceFor(trigger) {
    var card = trigger.closest('.cart-suggestion--group');
    if (card) return card.querySelectorAll('.cart-suggestion__color');

    var row = trigger.closest('.pqo-colours');
    return row ? row.children : [];
  }

  /* Reads both shapes: a card swatch (aria-controls + .sr-only name) and an
     entry in a row built below, including the current one, which is not a
     button and so carries its target in a data attribute instead. */
  function colourInfo(el) {
    var srOnly = el.querySelector('.sr-only');
    return {
      id: el.getAttribute('aria-controls') || el.getAttribute('data-pqo-target') || '',
      handle: el.getAttribute(LAZY_ATTR) || '',
      label: el.getAttribute('aria-label') || (srOnly ? srOnly.textContent.trim() : ''),
      image: el.querySelector('img')
    };
  }

  function paintColours(overlay, trigger) {
    if (overlay.querySelector('.pqo-colours')) return;

    var swatches = colourSourceFor(trigger);
    if (swatches.length < 2) return;

    var sheet = overlay.querySelector('.pqo-sheet');
    if (!sheet) return;

    var row = document.createElement('div');
    row.className = 'pqo-colours';

    Array.prototype.forEach.call(swatches, function (swatch) {
      var colour = colourInfo(swatch);
      var isCurrent = colour.id === overlay.id;
      var el = document.createElement(isCurrent ? 'span' : 'button');

      el.className = 'pqo-colour' + (isCurrent ? ' pqo-colour--current' : '');
      el.setAttribute(LAZY_ATTR, colour.handle);
      if (colour.label) el.setAttribute('aria-label', colour.label);

      if (isCurrent) {
        el.setAttribute('aria-current', 'true');
        // Not a control, so it cannot say aria-controls — but the next sheet
        // built from this row still needs to know which colour it is.
        el.setAttribute('data-pqo-target', colour.id);
      } else {
        el.type = 'button';
        el.setAttribute('aria-controls', colour.id);
      }

      if (colour.image) el.appendChild(colour.image.cloneNode(true));
      row.appendChild(el);
    });

    // Where the Liquid put it: above the sizes, so it reads colour, size, buy.
    var form = sheet.querySelector('form');
    if (form) form.parentNode.insertBefore(row, form);
    else sheet.appendChild(row);
  }

  function lazyTriggerFor(node) {
    if (!node || !node.closest) return null;
    var trigger = node.closest('[' + LAZY_ATTR + '][aria-controls]');
    if (!trigger) return null;
    // Already on the page: the normal open handler takes it from here.
    return document.getElementById(trigger.getAttribute('aria-controls')) ? null : trigger;
  }

  /* Capture phase: snowballing the click through to the opener would find no
     sheet and do nothing at all, which is exactly how a tap on a card used to
     feel when its overlay was missing. */
  document.addEventListener('click', function (event) {
    var trigger = lazyTriggerFor(event.target);
    if (!trigger) return;

    var handle = trigger.getAttribute(LAZY_ATTR);
    if (!handle) return;

    event.preventDefault();
    event.stopPropagation();

    fetchOverlay(handle).then(function () {
      var overlay = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!overlay) return; // response was for another product — nothing to open

      paintColours(overlay, trigger);
      trigger.click(); // the sheet exists now, so this runs the normal open path
    }).catch(function () {});
  }, true);

  ['pointerenter', 'touchstart', 'focusin'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var trigger = lazyTriggerFor(event.target);
      if (!trigger) return;

      var handle = trigger.getAttribute(LAZY_ATTR);
      if (handle) fetchOverlay(handle).catch(function () {});
    }, { capture: true, passive: true });
  });

  /* ---------- multi-option picker (no form, fetch-based ATC) ---------- */

  function moGetSel(did) {
    var s = {};
    document.querySelectorAll('[data-pqo-mo="' + did + '"]:checked').forEach(function (r) {
      s[parseInt(r.dataset.optionPos)] = r.value;
    });
    return s;
  }

  function moResolve(did, variants) {
    var optCount = variants.length ? variants[0].options.length : 0;
    var s = moGetSel(did);
    if (Object.keys(s).length < optCount) return null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i], m = true;
      for (var p in s) { if (v.options[p - 1] !== s[p]) { m = false; break; } }
      if (m) return v;
    }
    return null;
  }

  /* Every value stays visible: sold out everywhere → struck through and unselectable;
     in stock but not with the current picks → greyed, and clicking it moves the others. */
  function moMark(did) {
    var ol = document.getElementById(did);
    if (!ol) return;
    var variants = variantList(ol);
    var s = moGetSel(did);
    ol.querySelectorAll('[data-pqo-mo="' + did + '"]').forEach(function (r) {
      var p = parseInt(r.dataset.optionPos), val = r.value;
      var anywhere = false, withCurrent = false;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v.qty <= 0) continue;
        if (v.options[p - 1] !== val) continue;
        anywhere = true;
        var om = true;
        for (var op in s) {
          if (parseInt(op) === p) continue;
          if (v.options[op - 1] !== s[op]) { om = false; break; }
        }
        if (om) { withCurrent = true; break; }
      }
      var lb = r.nextElementSibling;
      r.disabled = !anywhere;
      if (lb) {
        lb.classList.toggle('is-disabled', !anywhere);
        lb.classList.toggle('is-unavailable', anywhere && !withCurrent);
      }
    });
  }

  /* Move the other options to the closest in-stock combination containing this value */
  function moReconcile(did, pos, val) {
    var ol = document.getElementById(did);
    if (!ol) return;
    var variants = variantList(ol);
    var s = moGetSel(did);
    var best = null, bestScore = -1;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.qty <= 0 || v.options[pos - 1] !== val) continue;
      var score = 0;
      for (var p in s) {
        if (parseInt(p) === pos) continue;
        if (v.options[p - 1] === s[p]) score++;
      }
      if (score > bestScore) { bestScore = score; best = v; }
    }
    if (!best) return;
    ol.querySelectorAll('[data-pqo-mo="' + did + '"]').forEach(function (r) {
      var p = parseInt(r.dataset.optionPos);
      if (r.value === best.options[p - 1]) r.checked = true;
    });
  }

  function moUpdate(did) {
    moMark(did);
    var ol = document.getElementById(did);
    if (!ol) return;
    var v = moResolve(did, variantList(ol));
    var pe = document.querySelector('[data-pqo-price="' + did + '"]');
    if (v && pe) pe.textContent = v.price;
    if (v && window._pqoSetHeaderPrice) window._pqoSetHeaderPrice(ol, v.id);
  }

  /* ---------- delegated change handling ---------- */

  document.addEventListener('change', function (e) {
    var t = e.target;

    /* qty-breaks size swatch active state */
    if (t.matches('[data-pqo-qb-option]')) {
      var group = t.closest('.qty-breaks__size-options');
      if (group) {
        group.querySelectorAll('.block-swatch').forEach(function (b) {
          b.classList.toggle('is-active-qb', b.querySelector('input').checked);
        });
      }
      return;
    }

    /* multi-option radio */
    if (t.matches('[data-pqo-mo]')) {
      var moDid = t.getAttribute('data-pqo-mo');
      var picked = moResolve(moDid, variantList(document.getElementById(moDid)));
      if (!picked || picked.qty <= 0) moReconcile(moDid, parseInt(t.dataset.optionPos), t.value);
      moUpdate(moDid);
    }
  });

  /* ---------- delegated click handling ---------- */

  document.addEventListener('click', function (e) {
    /* qty-breaks tier select */
    var tier = e.target.closest('[data-pqo-qb-tier]');
    if (tier && !e.target.closest('.block-swatch')) {
      var wrap = tier.closest('[data-pqo-qb]');
      if (wrap) {
        var did = wrap.getAttribute('data-pqo-qb');
        wrap.querySelectorAll('[data-pqo-qb-tier]').forEach(function (t) {
          t.classList.remove('is-selected');
          var s = t.querySelector('[data-pqo-qb-sizes]');
          if (s) s.style.display = 'none';
        });
        tier.classList.add('is-selected');
        var sizes = tier.querySelector('[data-pqo-qb-sizes]');
        if (sizes) sizes.style.display = '';
        var priceEl = wrap.querySelector('[data-pqo-qb-atc-price="' + did + '"]');
        if (priceEl) priceEl.textContent = formatPrice(parseInt(tier.dataset.pqoQbPrice, 10));
      }
    }

    /* qty-breaks add to cart */
    var qbAtc = e.target.closest('[data-pqo-qb-atc]');
    if (qbAtc) {
      var qbDid = qbAtc.getAttribute('data-pqo-qb-atc');
      var qbWrap = qbAtc.closest('[data-pqo-qb]') || document.querySelector('[data-pqo-qb="' + qbDid + '"]');
      var overlay = document.getElementById(qbDid);
      if (!qbWrap || !overlay) return;
      var selectedTier = qbWrap.querySelector('[data-pqo-qb-tier].is-selected');
      if (!selectedTier) return;
      var qty = parseInt(selectedTier.dataset.pqoQbQty, 10);
      var sizeContainer = selectedTier.querySelector('[data-pqo-qb-sizes]');
      var vid = defaultVariantId(overlay);
      if (sizeContainer) {
        var opts = Array.from(sizeContainer.querySelectorAll('[data-pqo-qb-option]:checked'))
          .sort(function (a, b) { return parseInt(a.dataset.pqoQbOptionPos) - parseInt(b.dataset.pqoQbOptionPos); });
        if (opts.length) {
          var sel = opts.map(function (r) { return r.value; });
          var variants = variantList(overlay);
          for (var i = 0; i < variants.length; i++) {
            var v = variants[i], m = v.options.length === sel.length;
            for (var j = 0; m && j < sel.length; j++) { if (v.options[j] !== sel[j]) m = false; }
            if (m) { vid = v.id; break; }
          }
        }
      }
      if (!vid) return;
      postAdd(qbAtc, vid, qty, { did: qbDid, resetOverflow: true, showDelay: 400, errLabel: 'PQO QtyBreaks ATC error' });
      return;
    }

    /* multi-option add to cart */
    var moBtn = e.target.closest('[data-pqo-mo-atc]');
    if (moBtn) {
      var moDid = moBtn.getAttribute('data-pqo-mo-atc');
      var moOverlay = document.getElementById(moDid);
      if (!moOverlay) return;
      var moVariant = moResolve(moDid, variantList(moOverlay));
      if (!moVariant) return;
      postAdd(moBtn, moVariant.id, 1, { did: moDid, resetOverflow: false, showDelay: 500, errLabel: 'ATC error' });
    }
  });

  /* ---------- initial availability marking for multi-option pickers ---------- */

  function initOverlay(overlay) {
    if (!overlay.id) return;
    if (overlay.querySelector('[data-pqo-multi="' + overlay.id + '"]')) moUpdate(overlay.id);
  }

  function initAll(root) {
    (root || document).querySelectorAll('.pqo-overlay').forEach(initOverlay);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  /* Instances injected later via innerHTML (lazy cart drawer): <script> tags never
     execute there, so watch for new overlays and run the initial filter for them. */
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.classList && n.classList.contains('pqo-overlay')) initOverlay(n);
        else if (n.querySelectorAll) n.querySelectorAll('.pqo-overlay').forEach(initOverlay);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
